import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './SurveyStart.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { startSurveyAction, getAttemptSurveyQuestionsListAction, isLastQuestionAction, START_SURVEY_RESET } from '../redux/attemptSurvey/attemptSurvey.actions';
import { getProfileDataAction } from '../redux/auth/auth.actions';
import SurveyQuestion from '../components/StartSurveyComponents/SurveyQuestion';
import SurveySponsors from '../components/StartSurveyComponents/SurveySponsers';
import SurveyPrivacy from '../components/StartSurveyComponents/SurveyPrivacy';
import SurveyPrimaryQuestion from '../components/StartSurveyComponents/SurveyPrimaryQuestion';
import SurveyCaracteristics from '../components/StartSurveyComponents/SurveyCaracteristics';
import Loader from '../loader/Loader';
import { toast } from 'react-toastify';
import {
  isDirectEliminationSurvey,
  isPreliminarySectionComplete,
  isPreliminaryFailed,
  hasStartedSurvey,
  hasPreliminaryQuestions,
} from '../helper/preliminarySurvey';
import { getAppLanguage } from '../helper/language';

const TEXTS = {
    Italian: {
        timeExpiredBody: "Non hai superato la domanda preliminare, quindi non puoi proseguire con le altre domande del sondaggio.",
        thankYou: "Grazie",
        surveyCompleted: "Hai completato con successo il sondaggio.",
        yourSurveys: "I tuoi Sondaggi",
        browseSurveys: "Sfoglia altri sondaggi"
    },
    English: {
        timeExpiredBody: "You are failed in preliminary question so you can not attempt further question of survey.",
        thankYou: "Thank you",
        surveyCompleted: "You have successfully completed the survey.",
        yourSurveys: "Your Surveys",
        browseSurveys: "Browse other surveys"
    }
};

const PAGES = {
    SPONSORS: 0,
    PRIVACY: 1,
    PRELIMINARY: 2,
    SIMPLE: 3,
    THANK_YOU: 4,
    CHARACTERISTICS: 5
};

export default function SurveyStart({ startSurvey, handleStartSurveyForDetails, initialSurveyProgress, isResumingSurvey }) {
    const { id } = useParams();
    const [start, setStart] = useState(false);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(PAGES.SPONSORS);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [privacy, setPrivacy] = useState('');
    const [question1, setQuestion1] = useState('');
    const [question2, setQuestion2] = useState('');
    const [rating, setRating] = useState(1);
    const [comment, setComment] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [isRefreshed, setIsRefreshed] = useState(false);
    const [preliminaryQuestionIndex, setPreliminaryQuestionIndex] = useState(0);
    const [characteristicQuestionIndex, setCharacteristicQuestionIndex] = useState(0);
    const [preliminaryComplete, setPreliminaryComplete] = useState(false);
    const [characteristicsComplete, setCharacteristicsComplete] = useState(false);
    const [mainComplete, setMainComplete] = useState(false);
    const [isAttempted, setIsAttempted] = useState(false);
    const [questionAnswers, setQuestionAnswers] = useState({});
    const [questionComments, setQuestionComments] = useState({});

    const dispatch = useDispatch();
    const details = useSelector((state) => state.surveyInfo.details);
    const attemptSurveyInfo = useSelector((state) => state.attemptSurveyInfo);
    const {
        startSurveyData,
        isLoading: startSurveyLoading,
        hasUserParticipatedInSurvey,
        surveyQuestions,
        surveySteps: globalSurveySteps
    } = attemptSurveyInfo;
    const language = getAppLanguage();

    const saveQuestionAnswer = useCallback((questionId, answer, answerComment = '') => {
        setQuestionAnswers(prev => ({
            ...prev,
            [questionId]: answer
        }));

        if (answerComment) {
            setQuestionComments(prev => ({
                ...prev,
                [questionId]: answerComment
            }));
        }

        const savedAnswers = JSON.parse(localStorage.getItem('surveyAnswers') || '{}');
        const savedComments = JSON.parse(localStorage.getItem('surveyComments') || '{}');

        savedAnswers[questionId] = answer;
        if (answerComment) {
            savedComments[questionId] = answerComment;
        }

        localStorage.setItem('surveyAnswers', JSON.stringify(savedAnswers));
        localStorage.setItem('surveyComments', JSON.stringify(savedComments));
    }, []);

    const restoreQuestionAnswer = useCallback((questionId) => {
        setSelectedOption(null);
        setSelectedOptions([]);
        setComment('');

        const savedAnswers = JSON.parse(localStorage.getItem('surveyAnswers') || '{}');
        const savedComments = JSON.parse(localStorage.getItem('surveyComments') || '{}');

        const savedAnswer = savedAnswers[questionId];
        const savedComment = savedComments[questionId];

        if (savedAnswer) {
            if (Array.isArray(savedAnswer)) {
                setSelectedOptions(savedAnswer);
            } else {
                setSelectedOption(savedAnswer);
            }
        }

        if (savedComment) {
            setComment(savedComment);
        }
    }, []);

    const clearQuestionAnswer = useCallback(() => {
        setSelectedOption(null);
        setSelectedOptions([]);
        setComment('');
        setRating(1);
        setIsAttempted(false);
    }, []);

    const clearAllSurveyData = useCallback(() => {
        setQuestionAnswers({});
        setQuestionComments({});
        localStorage.removeItem('surveyAnswers');
        localStorage.removeItem('surveyComments');
        clearQuestionAnswer();
    }, [clearQuestionAnswer]);

    useEffect(() => {
        dispatch({ type: START_SURVEY_RESET });
    }, [dispatch, id]);

    useEffect(() => {
        setCurrentQuestionIndex(0);
        setPreliminaryQuestionIndex(0);
        setCharacteristicQuestionIndex(0);
        setCurrentPage(PAGES.SPONSORS);
        setPrivacy('');
        setSelectedOptions([]);
        setComment('');
        setRating(1);
        setIsAttempted(false);
        setPreliminaryComplete(false);
        setCharacteristicsComplete(false);
        setMainComplete(false);
        clearAllSurveyData();
    }, [id, clearAllSurveyData]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (e.type === 'beforeunload') {
                setIsRefreshed(true);
            }
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const handlePrivacyChange = (e) => {
        const { id } = e.target;
        setPrivacy(id);
    };

    useEffect(() => {
        if (currentPage === PAGES.THANK_YOU) {
            dispatch(getProfileDataAction());
        }
    }, [currentPage, dispatch]);

    const { profileData } = useSelector((state) => state.authInfo);

    const handleRadioChange = (questionKey, value) => {
        switch (questionKey) {
            case 'question1':
                setQuestion1(value);
                break;
            case 'question2':
                setQuestion2(value);
                break;
            default:
                break;
        }
    };

    const handleCheckboxChange = (option) => {
        setSelectedOptions(prev => {
            if (prev.includes(option)) {
                return prev.filter(item => item !== option);
            }
            return [...prev, option];
        });
    };

    useEffect(() => {
        if (!surveyQuestions?.data && !details?._id) {
            dispatch(getAttemptSurveyQuestionsListAction(id));
        }
    }, [dispatch, surveyQuestions?.data, details?._id, id]);

    const surveyData = surveyQuestions?.data || details;
    const questionList = surveyData?.questions || [];
    const characteristicQuestions = surveyData?.caracteristics || [];
    const preliminaryQuestions = surveyData?.preliminaries || [];
    const hasPreliminary = hasPreliminaryQuestions(surveyData);
    const hasCharacteristics = characteristicQuestions.length > 0;
    const hasSimpleQuestions = questionList.length > 0;
    const isDirectElimination = isDirectEliminationSurvey(surveyData, details);
    const surveyStarted = hasStartedSurvey(startSurveyData);

    const resolveSurveySteps = useCallback((stepsSource) => {
        if (!stepsSource) return null;
        const currentUserId = profileData?.data?._id;
        if (Array.isArray(stepsSource)) {
            if (!stepsSource.length) return null;
            if (currentUserId) {
                const userEntry = stepsSource.find(step =>
                    step?.createdBy === currentUserId ||
                    step?.userId === currentUserId
                );
                if (userEntry) return userEntry;
            }
            return stepsSource[0];
        }
        if (typeof stepsSource === 'object') {
            if (
                !currentUserId ||
                stepsSource?.createdBy === currentUserId ||
                stepsSource?.userId === currentUserId
            ) {
                return stepsSource;
            }
        }
        return null;
    }, [profileData?.data?._id]);

    const surveyProgress = useMemo(() => {
        if (initialSurveyProgress) return initialSurveyProgress;
        return (
            resolveSurveySteps(surveyData?.user_steps) ||
            resolveSurveySteps(surveyData?.surveySteps) ||
            resolveSurveySteps(details?.user_steps) ||
            resolveSurveySteps(details?.surveySteps) ||
            resolveSurveySteps(globalSurveySteps)
        );
    }, [
        resolveSurveySteps,
        surveyData?.user_steps,
        surveyData?.surveySteps,
        details?.user_steps,
        details?.surveySteps,
        globalSurveySteps,
        initialSurveyProgress
    ]);

    const preliminaryStatusSources = useMemo(
        () => [surveyProgress, surveyData, details, initialSurveyProgress],
        [surveyProgress, surveyData, details, initialSurveyProgress]
    );

    // Load saved answers from localStorage when component mounts
    useEffect(() => {
        const savedAnswers = JSON.parse(localStorage.getItem('surveyAnswers') || '{}');
        const savedComments = JSON.parse(localStorage.getItem('surveyComments') || '{}');

        if (Object.keys(savedAnswers).length > 0) {
            setQuestionAnswers(savedAnswers);
        }
        if (Object.keys(savedComments).length > 0) {
            setQuestionComments(savedComments);
        }
    }, []);

    // Restore answer when question index changes
    useEffect(() => {
        if (questionList.length > 0 && currentQuestionIndex < questionList.length) {
            const currentQuestion = questionList[currentQuestionIndex];
            if (currentQuestion) {
                restoreQuestionAnswer(currentQuestion._id);
            }
        }
    }, [currentQuestionIndex, questionList]);

    const goToPreliminarySection = useCallback(async () => {
        if (!surveyStarted) {
            await dispatch(startSurveyAction(id, privacy || 'private'));
        }
        setPreliminaryQuestionIndex(0);
        clearQuestionAnswer();
        setCurrentPage(PAGES.PRELIMINARY);
    }, [clearQuestionAnswer, dispatch, id, privacy, surveyStarted]);

    const goToCharacteristicsSection = useCallback(() => {
        setCharacteristicQuestionIndex(0);
        clearQuestionAnswer();
        setCurrentPage(PAGES.CHARACTERISTICS);
    }, [clearQuestionAnswer]);

    const goToSimpleSection = useCallback(() => {
        if (!hasSimpleQuestions) {
            setCurrentPage(PAGES.THANK_YOU);
            return;
        }
        setCurrentQuestionIndex(0);
        clearQuestionAnswer();
        setCurrentPage(PAGES.SIMPLE);
    }, [clearQuestionAnswer, hasSimpleQuestions]);

    const goToThankYou = useCallback(() => {
        setCurrentPage(PAGES.THANK_YOU);
    }, []);

    const beginQuestionFlow = useCallback(() => {
        if (hasPreliminary && !preliminaryComplete) {
            goToPreliminarySection();
            return;
        }
        if (hasCharacteristics && !characteristicsComplete) {
            goToCharacteristicsSection();
            return;
        }
        if (!mainComplete && hasSimpleQuestions) {
            goToSimpleSection();
            return;
        }
        goToThankYou();
    }, [
        goToCharacteristicsSection,
        goToPreliminarySection,
        goToSimpleSection,
        goToThankYou,
        hasCharacteristics,
        hasPreliminary,
        hasSimpleQuestions,
        preliminaryComplete,
        characteristicsComplete,
        mainComplete
    ]);

    const handleStartSurvey = async () => {
        if (isDirectElimination && hasPreliminary && !surveyStarted) {
            await dispatch(startSurveyAction(id, privacy || 'private'));
            beginQuestionFlow();
            return;
        }
        await dispatch(startSurveyAction(id, privacy));
    };

    useEffect(() => {
        if (startSurveyData?.data?.preliminaryAttempted && !isPreliminaryFailed(...preliminaryStatusSources)) {
            setPreliminaryComplete(true);
        }
    }, [startSurveyData?.data?.preliminaryAttempted, surveyProgress, surveyData, details, initialSurveyProgress, preliminaryStatusSources]);

    useEffect(() => {
        if (!surveyData && !surveyProgress) return;

        if (!surveyProgress) {
            setPreliminaryComplete(!hasPreliminary);
            setCharacteristicsComplete(!hasCharacteristics);
            setMainComplete(!hasSimpleQuestions);
            return;
        }

        if (!preliminaryQuestions.length) return;

        const preComplete = isPreliminarySectionComplete(
            surveyProgress,
            preliminaryQuestions.length,
            ...preliminaryStatusSources
        );
        setPreliminaryComplete(preComplete);
        if (!preComplete && hasPreliminary) {
            const completedPre = Number(surveyProgress?.preliminaryStep ?? 0);
            const nextPreIndex = Math.min(
                isPreliminaryFailed(...preliminaryStatusSources) ? 0 : completedPre,
                Math.max(preliminaryQuestions.length - 1, 0)
            );
            setPreliminaryQuestionIndex(nextPreIndex);
        }

        const totalChar = Number(surveyProgress?.totalCarachteristics ?? characteristicQuestions.length);
        const completedChar = Number(surveyProgress?.carachteristicsStep ?? 0);
        const charComplete = !hasCharacteristics || completedChar >= totalChar;
        setCharacteristicsComplete(charComplete);
        if (!charComplete && hasCharacteristics) {
            const nextCharIndex = Math.min(
                completedChar,
                Math.max(characteristicQuestions.length - 1, 0)
            );
            setCharacteristicQuestionIndex(nextCharIndex);
        }

        const totalSimple = Number(surveyProgress?.totalSurvey ?? questionList.length);
        const completedSimple = Number(surveyProgress?.surveyStep ?? 0);
        const simpleComplete = hasSimpleQuestions && totalSimple > 0 && completedSimple >= totalSimple;
        setMainComplete(simpleComplete);
        if (!simpleComplete && hasSimpleQuestions) {
            const nextSimpleIndex = Math.min(
                completedSimple,
                Math.max(questionList.length - 1, 0)
            );
            setCurrentQuestionIndex(nextSimpleIndex);
        }
    }, [
        surveyProgress,
        surveyData,
        hasPreliminary,
        hasCharacteristics,
        hasSimpleQuestions,
        preliminaryQuestions.length,
        characteristicQuestions.length,
        questionList.length,
        preliminaryStatusSources
    ]);

    useEffect(() => {
        if (currentPage !== PAGES.PRIVACY) return;
        if (!surveyData) return;

        if (isDirectElimination && hasPreliminary && !surveyStarted) {
            beginQuestionFlow();
            return;
        }

        if (!surveyStarted) return;
        beginQuestionFlow();
    }, [
        beginQuestionFlow,
        currentPage,
        hasPreliminary,
        isDirectElimination,
        surveyData,
        surveyStarted
    ]);

    useEffect(() => {
        if (currentPage !== PAGES.PRELIMINARY) return;
        if (preliminaryComplete) {
            beginQuestionFlow();
        }
    }, [beginQuestionFlow, currentPage, preliminaryComplete]);

    useEffect(() => {
        if (!isResumingSurvey || !initialSurveyProgress || !surveyData) return;

        const totalPre = Number(initialSurveyProgress?.totalPreliminary ?? preliminaryQuestions.length);
        const completedPre = Number(initialSurveyProgress?.preliminaryStep ?? 0);
        const totalChar = Number(initialSurveyProgress?.totalCarachteristics ?? characteristicQuestions.length);
        const completedChar = Number(initialSurveyProgress?.carachteristicsStep ?? 0);
        const totalSimple = Number(initialSurveyProgress?.totalSurvey ?? questionList.length);
        const completedSimple = Number(initialSurveyProgress?.surveyStep ?? 0);

        const preComplete = isPreliminarySectionComplete(
            initialSurveyProgress,
            preliminaryQuestions.length,
            ...preliminaryStatusSources
        );

        if (hasPreliminary && !preComplete) {
            setCurrentPage(PAGES.PRELIMINARY);
            return;
        }
        if (hasCharacteristics && completedChar < totalChar) {
            setCurrentPage(PAGES.CHARACTERISTICS);
            return;
        }
        if (hasSimpleQuestions && completedSimple < totalSimple) {
            setCurrentPage(PAGES.SIMPLE);
        }
    }, [
        characteristicQuestions.length,
        hasCharacteristics,
        hasPreliminary,
        hasSimpleQuestions,
        initialSurveyProgress,
        isResumingSurvey,
        preliminaryQuestions.length,
        questionList.length,
        surveyData,
        initialSurveyProgress,
        surveyProgress,
        details
    ]);

    const handleNextQuestion = () => {
        const currentQuestion = questionList[currentQuestionIndex];
        if (currentQuestion) {
            const answer = selectedOptions.length > 0 ? selectedOptions : selectedOption;
            saveQuestionAnswer(currentQuestion._id, answer, comment);
        }

        const isLastQuestion = currentQuestionIndex === questionList.length - 1;

        if (!isLastQuestion) {
            const nextIndex = currentQuestionIndex + 1;
            setCurrentQuestionIndex(nextIndex);
            const nextQuestion = questionList[nextIndex];
            if (nextQuestion) {
                restoreQuestionAnswer(nextQuestion._id);
            } else {
                clearQuestionAnswer();
            }
            if (nextIndex === questionList.length - 1) {
                dispatch(isLastQuestionAction(id));
            }
            return;
        }

        dispatch(isLastQuestionAction(id));
        clearAllSurveyData();
        setMainComplete(true);
        goToThankYou();
    };

    const handlePreviousQuestion = () => {
        const currentQuestion = questionList[currentQuestionIndex];
        if (currentQuestion) {
            const answer = selectedOptions.length > 0 ? selectedOptions : selectedOption;
            saveQuestionAnswer(currentQuestion._id, answer, comment);
        }

        if (currentQuestionIndex > 0) {
            const previousIndex = currentQuestionIndex - 1;
            setCurrentQuestionIndex(previousIndex);
            const prevQuestion = questionList[previousIndex];
            if (prevQuestion) {
                restoreQuestionAnswer(prevQuestion._id);
            } else {
                clearQuestionAnswer();
            }
        }
    };

    const handlePreliminaryFail = (apiMessage) => {
        const message = apiMessage || TEXTS[language].timeExpiredBody;
        toast.error(message, {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
        });
        setTimeout(() => {
            navigate('/sondaggi');
        }, 2000);
    };

    const handleNextPreliminaryQuestion = async () => {
        const currentPrelimQuestion = preliminaryQuestions[preliminaryQuestionIndex];
        if (!currentPrelimQuestion) return;

        const isLastPreliminary = preliminaryQuestionIndex === preliminaryQuestions.length - 1;

        if (!isLastPreliminary) {
            const nextIndex = preliminaryQuestionIndex + 1;
            setPreliminaryQuestionIndex(nextIndex);
            if (nextIndex === preliminaryQuestions.length - 1) {
                dispatch(isLastQuestionAction(id));
            }
            clearQuestionAnswer();
            return;
        }

        dispatch(isLastQuestionAction(id));
        setPreliminaryComplete(true);
        clearQuestionAnswer();

        if (hasCharacteristics && !characteristicsComplete) {
            goToCharacteristicsSection();
        } else if (hasSimpleQuestions) {
            goToSimpleSection();
        } else {
            goToThankYou();
        }
    };

    const handlePreviousPreliminaryQuestion = () => {
        if (preliminaryQuestionIndex > 0) {
            setPreliminaryQuestionIndex(preliminaryQuestionIndex - 1);
            setSelectedOption(null);
            setSelectedOptions([]);
            setIsAttempted(false);
        }
    };

    const handleNextCharacteristicQuestion = () => {
        if (!characteristicQuestions.length) return;
        const isLastCharacteristic = characteristicQuestionIndex === characteristicQuestions.length - 1;

        if (!isLastCharacteristic) {
            const nextIndex = characteristicQuestionIndex + 1;
            setCharacteristicQuestionIndex(nextIndex);
            if (nextIndex === characteristicQuestions.length - 1) {
                dispatch(isLastQuestionAction(id));
            }
            clearQuestionAnswer();
            return;
        }

        dispatch(isLastQuestionAction(id));
        setCharacteristicsComplete(true);
        clearQuestionAnswer();

        if (hasPreliminary && !preliminaryComplete) {
            goToPreliminarySection();
        } else if (hasSimpleQuestions) {
            goToSimpleSection();
        } else {
            goToThankYou();
        }
    };

    const handlePreviousCharacteristicQuestion = () => {
        if (characteristicQuestionIndex > 0) {
            setCharacteristicQuestionIndex(characteristicQuestionIndex - 1);
            setSelectedOption(null);
            setSelectedOptions([]);
            setIsAttempted(false);
        }
    };

    const handleNavigateToSurvey = (e) => {
        navigate("/sondaggi");
        setCurrentPage(PAGES.SPONSORS);
    };

    const renderCurrentPage = () => {
        switch (currentPage) {
            case PAGES.SPONSORS:
                return <SurveySponsors
                    id={id}
                    details={details}
                    setStart={setStart}
                    setCurrentPage={setCurrentPage}
                    startSurvey={startSurvey}
                    handleStartSurveyForDetails={handleStartSurveyForDetails}
                />;

            case PAGES.PRIVACY:
                return <SurveyPrivacy
                    key="privacy"
                    privacy={privacy}
                    onPrivacyChange={handlePrivacyChange}
                    onStartSurvey={handleStartSurvey}
                    id={id}
                    setCurrentPage={setCurrentPage}
                    isLoading={startSurveyLoading}
                />;

            case PAGES.CHARACTERISTICS:
                if (characteristicQuestions?.length > 0 && characteristicQuestions[characteristicQuestionIndex]) {
                    const currentCharacteristicQuestion = characteristicQuestions[characteristicQuestionIndex];
                    return <SurveyCaracteristics
                        characteristicQuestionId={currentCharacteristicQuestion._id}
                        comment={comment}
                        setComment={setComment}
                        survey={id}
                        isCharacteristic={true}
                        setSelectedOption={setSelectedOption}
                        setSelectedOptions={setSelectedOptions}
                        selectedOptions={selectedOptions}
                        handleCheckboxChange={handleCheckboxChange}
                        questionList={characteristicQuestions}
                        selectedOption={selectedOption}
                        key={characteristicQuestionIndex}
                        questionIndex={characteristicQuestionIndex}
                        question={currentCharacteristicQuestion.question}
                        mandatory={currentCharacteristicQuestion.mandatory}
                        options={currentCharacteristicQuestion.options}
                        counts={currentCharacteristicQuestion.counts}
                        onChange={(opt) => handleRadioChange(currentCharacteristicQuestion.question, opt.value)}
                        onNext={handleNextCharacteristicQuestion}
                        onPrevious={handlePreviousCharacteristicQuestion}
                    />;
                }
                return null;

            case PAGES.PRELIMINARY:
                if (!surveyData || (hasPreliminary && !preliminaryQuestions.length)) {
                    return <Loader />;
                }
                if (preliminaryQuestions?.length > 0 && !preliminaryComplete && preliminaryQuestions[preliminaryQuestionIndex]) {
                    const currentPrelimQuestion = preliminaryQuestions[preliminaryQuestionIndex];
                    return <SurveyPrimaryQuestion
                        preliminaryQuestionId={currentPrelimQuestion._id}
                        comment={comment}
                        setComment={setComment}
                        survey={id}
                        isPreliminary={true}
                        setSelectedOption={setSelectedOption}
                        setSelectedOptions={setSelectedOptions}
                        selectedOptions={selectedOptions}
                        handleCheckboxChange={handleCheckboxChange}
                        questionList={preliminaryQuestions}
                        selectedOption={selectedOption}
                        key={preliminaryQuestionIndex}
                        questionIndex={preliminaryQuestionIndex}
                        question={currentPrelimQuestion.question}
                        mandatory={currentPrelimQuestion.mandatory}
                        options={currentPrelimQuestion.options}
                        points={currentPrelimQuestion.points}
                        maxTime={currentPrelimQuestion.maxTime}
                        skipWelcomeModal={isResumingSurvey}
                        onChange={(opt) => handleRadioChange(currentPrelimQuestion.question, opt.value)}
                        onNext={handleNextPreliminaryQuestion}
                        onPrevious={handlePreviousPreliminaryQuestion}
                        onFail={handlePreliminaryFail}
                    />;
                }
                return null;

            case PAGES.SIMPLE:
                if (questionList && questionList[currentQuestionIndex]) {
                    const currentQuestion = questionList[currentQuestionIndex];
                    return <SurveyQuestion
                        comment={comment}
                        setComment={setComment}
                        webUrls={currentQuestion.webUrls}
                        urls={currentQuestion.urls}
                        survey={id}
                        setSelectedOption={setSelectedOption}
                        setSelectedOptions={setSelectedOptions}
                        selectedOptions={selectedOptions}
                        handleCheckboxChange={handleCheckboxChange}
                        questionList={questionList}
                        selectedOption={selectedOption}
                        key={currentQuestionIndex}
                        questionIndex={currentQuestionIndex}
                        question={currentQuestion.title}
                        mandatory={currentQuestion.mandatory}
                        options={currentQuestion.options}
                        questionType={currentQuestion.questionType?.type}
                        onChange={(opt) => handleRadioChange(currentQuestion.title, opt.value)}
                        onNext={handleNextQuestion}
                        onPrevious={handlePreviousQuestion}
                        isAttempted={isAttempted}
                    />;
                }
                return null;

            case PAGES.THANK_YOU:
                return (
                    <div key="thankyou">
                        <div className="thankyou_wrapper">
                            <div className="icon">
                                <img src={`${process.env.PUBLIC_URL}/images/survey/thankyou.svg`} alt="" />
                            </div>
                            <div className="title">
                                {TEXTS[language].thankYou} <span>{profileData?.data?.name}</span>
                            </div>
                            <div className="content">{TEXTS[language].surveyCompleted}</div>
                            <div className="action">
                                <button onClick={handleNavigateToSurvey}>{TEXTS[language].yourSurveys}</button>
                                <button className="cancel" onClick={handleNavigateToSurvey}>{TEXTS[language].browseSurveys}</button>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="start_survey">
            {renderCurrentPage()}
        </div>
    );
}